import { useEffect, useRef } from 'react';
import type { ResultsRenderer, ResultsView, SearchResult } from '../../guest/src/widget.js';
import { safeText, safeUrl } from './protocol.js';
import type { Bridge } from './bridge.js';
import { createHeightReporter } from './height.js';
import { keyToAccommodationId } from './keys.js';

/**
 * The third renderer on the seam: `postMessage` out, no DOM of our own.
 * RETIRE-WITH-PRICECALCULATOR.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS AN ADAPTER AND NOT AN ARCHITECTURE
 * ---------------------------------------------------------------------------
 *
 * `tileResults` draws our own cards. `domResults` drives cards the host already
 * rendered, by touching its DOM. This one does exactly what `domResults` does —
 * hide the ones the filters excluded, stamp a price, wire a reservation link —
 * except that the cards are on the other side of an iframe boundary, so every
 * effect has to be requested rather than performed. Same `ResultsView` in; the
 * same decisions; a different verb.
 *
 * That is the whole reason the compat build is small. §10 of `decisions.md` had
 * already put the tiles on the host's side of the line for WordPress, and the
 * old HolidayMedia site happens to work the same way, so there is nothing new to
 * decide here.
 *
 * ---------------------------------------------------------------------------
 * THE ONE ORDERING RULE, WHICH IS NOT IN THE PROTOCOL DOC
 * ---------------------------------------------------------------------------
 *
 * `url` APPENDS. The parent does
 *
 *     span.appendChild(document.createElement('br'));
 *     const a = document.createElement('A'); a.href = arg; a.innerText = 'Reserveren';
 *     span.appendChild(a);
 *
 * so two `url` calls for one tile give the visitor two `Reserveren` links, and
 * there is no `unurl`. The doc says this. What it does not say is where the
 * eraser is: `pricetext` does `span.innerHTML = ''` before assigning the text,
 * and the appended link lives INSIDE that same span. So `pricetext` is the only
 * way to remove a link, and the invariant that makes the whole thing safe is
 *
 *     every `url` is immediately preceded by a `pricetext` for the same key.
 *
 * Which is enforced structurally below: `url` is only ever sent from inside the
 * branch that has just sent `pricetext`. Both facts were confirmed by running
 * the captured parent — see `test/postMessageResults.spec.tsx`.
 *
 * The corollary is the epoch. If the guest changes the dates and an
 * accommodation's price happens to be identical, the price text does not change,
 * so a naive diff sends nothing, so the old link survives — pointing at the old
 * dates. The epoch is arrival+nights and a change to it forces a re-send of
 * `pricetext` for every tile, which is what wipes the stale links.
 *
 * ---------------------------------------------------------------------------
 * WHY WE NEVER SEND `state`
 * ---------------------------------------------------------------------------
 *
 * The parent's handler is
 *
 *     history.replaceState(json, document.title, json.isDebug ? '?debug' : '?')
 *
 * so any `state` we send REWRITES THE HOST PAGE'S QUERY STRING to `?` — verified:
 * a page loaded as `?utm=x&gclid=y` ends up with an empty search. That destroys
 * the park's campaign attribution on every search a visitor runs. Keeping the
 * search in the parent's URL is not worth breaking their analytics, so this
 * renderer reads `state` (via the bridge) and never writes it.
 */

export interface PostMessageLabels {
  /** While a price is in flight. */
  pending: string;
  /** Matched and bookable, but we have no number. */
  noPrice: string;
  /** Matched but not available for these dates. */
  unavailable: string;
  /** A key on the page we could not map to an accommodation. */
  unknown: string;
  /** Every tile hidden: said in the frame, because the page shows nothing. */
  nothingFree: string;
}

export interface PostMessageResultsOptions {
  bridge: Bridge;
  labels: PostMessageLabels;
  /** How a price becomes a string. Injected: `ResultsView` has no locale. */
  money: (amount: number) => string;
  /**
   * The reservation link for one result, or null for no link.
   *
   * Given the whole view as well as the result, because the link has to carry the
   * dates and the party — a `Reserveren` link that drops them sends the guest
   * back to an empty form, which is the same as no link at all.
   */
  reservationUrl: (result: SearchResult, view: ResultsView) => string | null;
  /**
   * Hide a tile the guest cannot have, rather than labelling it.
   *
   * The old calculator hid them, the operator asked for the same, and it is the
   * better default: a guest who has just given dates wants what they CAN have,
   * and a row of greyed-out cards is noise they have to read past.
   *
   * False shows them with `labels.unavailable` instead — worth having for a
   * park with few types, where "it exists but not then" is itself useful.
   */
  hideUnavailable?: boolean;
  /** CMS key → accommodation id. Defaults to the marker convention. */
  resolveKey?: (key: string) => string;
  /** Where to measure. Injected in tests. */
  doc?: () => Document;
  warn?: (message: string) => void;
}

export function postMessageResults(options: PostMessageResultsOptions): ResultsRenderer {
  return (view: ResultsView) => <PostMessageBinder view={view} options={options} />;
}

interface Emitted {
  hidden: boolean;
  priceText: string;
  url: string | null;
  epoch: string;
}

function PostMessageBinder({ view, options }: {
  view: ResultsView;
  options: PostMessageResultsOptions;
}) {
  const { bridge, labels } = options;
  const hideUnavailable = options.hideUnavailable ?? true;
  const warn = options.warn ?? ((m: string) => { console.warn(m); });
  const resolveKey = options.resolveKey ?? keyToAccommodationId;

  // What the parent currently shows, per key. The parent reconciles nothing, so
  // this is the only record of it.
  const emitted = useRef(new Map<string, Emitted>());
  const unmappedWarned = useRef(new Set<string>());
  const height = useRef<ReturnType<typeof createHeightReporter> | null>(null);

  useEffect(() => {
    const reporter = createHeightReporter(
      (t, c, a) => bridge.send(t, c, a),
      options.doc ?? (() => document),
    );
    height.current = reporter;
    return () => { height.current = null; reporter.stop(); };
  }, [bridge, options.doc]);

  const report = () => height.current?.report();

  useEffect(() => {
    const keys = bridge.keys();
    // Nothing is known about the page yet. The bridge is still asking; every
    // tile is hidden already, which is the host's intended initial state.
    if (keys === null) { report(); return; }

    /**
     * An incomplete question gets no answer, and the answer to the previous
     * question is withdrawn.
     *
     * The host page hides every tile before our first message precisely because
     * an availability answer is meaningless until the guest has said when and
     * how long — the same rule as the guest widget's length-before-dates. If a
     * guest clears the dates, leaving the last search's prices on screen would
     * be worse than showing nothing, because those prices are answers to a
     * question that is no longer being asked.
     */
    if (view.arrival === null || view.nights === null) {
      if (emitted.current.size > 0) {
        emitted.current.clear();
        bridge.send('all', 'hide');
      }
      report();
      return;
    }

    const epoch = `${view.arrival}|${view.nights}`;
    const byId = new Map(view.all.map((r) => [r.accommodation.id, r]));
    let visibilityChanged = false;

    for (const key of keys) {
      const id = resolveKey(key);
      const result = byId.get(id);

      // An unmapped key is SHOWN, not left hidden. A hidden tile is the page's
      // own initial state, so an accommodation we cannot price would silently
      // become unsellable and nobody would find out; shown with "prijs op
      // aanvraag" it still has its own link and the park still sells it. The
      // warning is for us.
      const desired: Omit<Emitted, 'epoch'> = result === undefined
        ? { hidden: false, priceText: labels.unknown, url: null }
        : {
          /**
           * Hidden when the guest cannot have it, not merely when a filter
           * excluded it.
           *
           * NOT while loading. `available` is false for a moment before the
           * first quote lands, and hiding then showing again is a flicker the
           * guest reads as the page changing its mind.
           */
          hidden: !result.matched
            || (hideUnavailable && !result.loading && !result.available),
          priceText: priceTextFor(result, labels, options.money),
          /**
           * A link once the answer is SETTLED, priced or not.
           *
           * Not while loading, which was the original rule's point: a link
           * beside "de prijs is nog niet berekend" invites a guest to commit
           * to a number nobody has told them, and there is no `unurl` to take
           * it back with when the quote turns out to be unavailable.
           *
           * But "no price yet" and "this one is by arrangement" are not the
           * same state, and the rule treated them as one. REPORTED: "is niet
           * klikbaar", against a lodge reading "prijs op aanvraag" with every
           * tile beside it linked — so the one accommodation whose whole answer
           * is "ask us" was the one with no way to ask. The link goes to the
           * enquiry form either way; a price is not what makes it useful.
           *
           * Still nothing when the stay is unavailable. There is nothing to ask
           * about on those dates, and the tile says so.
           */
          url: result.matched && result.available && !result.loading
            ? options.reservationUrl(result, view)
            : null,
        };

      if (result === undefined && !unmappedWarned.current.has(key)) {
        unmappedWarned.current.add(key);
        warn(
          `compat-2027: the host page carries the marker {[${key}]}, which resolves to `
          + `accommodation id "${id}", and no such accommodation is published. The tile is `
          + `shown without a price. Either fix the caption or add an entry to KEY_ALIASES.`,
        );
      }

      const previous = emitted.current.get(key);
      if (previous === undefined || previous.hidden !== desired.hidden) {
        bridge.send(key, desired.hidden ? 'hide' : 'show');
        visibilityChanged = true;
      }

      // `pricetext` is also the eraser for a previously appended link, so it
      // goes out whenever EITHER the price or the link changed, and whenever the
      // question changed even if neither did.
      const stale = previous === undefined
        || previous.epoch !== epoch
        || previous.priceText !== desired.priceText
        || previous.url !== desired.url;

      if (!desired.hidden && stale) {
        bridge.send(key, 'pricetext', safeText(desired.priceText, warn));
        // The ONLY place `url` is sent. See the header.
        if (desired.url !== null) bridge.send(key, 'url', safeUrl(desired.url));
      }

      emitted.current.set(key, { ...desired, epoch });
    }

    // After every show/hide, because `show` clears the margin `beautify` sets,
    // so the parent has to recompute which tile is alone in a half-row.
    if (visibilityChanged) bridge.send('all', 'beautify');

    report();
  });

  /**
   * NORMALLY nothing of ours on this page — the tiles are the host's and the
   * prices go onto them.
   *
   * The exception is a search that hides every tile. On the host page that is a
   * silence: the form is in the frame, the tiles are gone, and nothing says
   * why. So the one thing this renderer draws is the sentence that explains an
   * empty page, and only when the page is empty.
   */
  const nothingLeft = view.arrival !== null && view.nights !== null
    && view.all.length > 0
    && view.all.every((r) => !r.matched || (hideUnavailable && !r.loading && !r.available));

  return nothingLeft ? <p className="pw-empty">{labels.nothingFree}</p> : null;
}

function priceTextFor(
  result: SearchResult,
  labels: PostMessageLabels,
  money: (amount: number) => string,
): string {
  if (result.loading) return labels.pending;
  // `price`, never `quote.total`: the API returns `total: 0` with
  // `status: 'unavailable'`, and stamping that on a tile advertises a free stay.
  if (result.price === null) return result.available ? labels.noPrice : labels.unavailable;
  return money(result.price);
}
